import React, { createContext, useEffect, useState } from 'react'
import { initialDataContext, GetInitialRootState } from '../helpers/ctxHelper'
import { handleMessage } from '../helpers/stateHelper'
import { IDataContext } from '../interfaces/IDataContext'
import { IRootState } from '../interfaces/IRootState'
import { api } from '../helpers/apiHelper'
import { t } from '../i18n/i18n'

export const DataContext = createContext(initialDataContext)

type DataContextProviderType = {
   children: JSX.Element | JSX.Element[]
}
export const DataContextProvider = ({ children }: DataContextProviderType) => {
   const [state, setRootState] = useState<IRootState>(GetInitialRootState())

   const dataContextValue: IDataContext = {
      state,
      setRootState,
      handleMessage,
   }

   const fetchScopedOrganisation = async (organisationId: string, signal?: AbortSignal) => {
      const [organisationResponse, defaultWorkplaceResponse, protectedAssetGoalsResponse, userAccountsResponse] = await Promise.all([
         api().getOrganisationById(organisationId, { signal }),
         api().getDefaultWorkplace(organisationId, { signal }),
         api().getProtectedAssetGoalsByOrganisationId(organisationId, { signal }),
         api().getUserAccountsByOrganisationId(organisationId, { signal }),
      ])

      setRootState((prev) => ({
         ...prev,
         organisation: organisationResponse.data ?? null,
         defaultWorkplace: defaultWorkplaceResponse.data ?? null,
         protectedAssetGoals: protectedAssetGoalsResponse.data ?? [],
         userAccounts: userAccountsResponse.data ?? [],
         initialLoading: false,
      }))
   }

   useEffect(() => {
      if (!state.organisation?.id) return

      const ac = new AbortController()
      fetchScopedOrganisation(state.organisation.id, ac.signal).catch((err: any) => {
         if (err?.name !== 'AbortError') {
            handleMessage(setRootState, 'error', t('SomethingWentWrong'), t('CouldNotFetchData'))
            setRootState((prev) => ({ ...prev, initialLoading: false }))
         }
      })
      return () => ac.abort()
   }, [state.organisation?.id])

   useEffect(() => {
      ;(async () => {
         try {
            dataContextValue.setRootState((prev) => ({ ...prev, initialLoading: true }))

            const userAccount = (await api().getMe()).data

            if (userAccount && userAccount?.organisationId) {
               setRootState((prev) => ({
                  ...prev,
                  userAccount,
                  // set id only, org useEffect will fetch the whole organisations data (and clear initialLoading)
                  organisation: { ...(prev.organisation ?? {}), id: userAccount.organisationId },
               }))
            } else {
               // No org to fetch — Phase 2 won't run, so clear initialLoading here
               setRootState((prev) => ({ ...prev, userAccount: userAccount ?? prev.userAccount, initialLoading: false }))
            }
         } catch (error: any) {
            handleMessage(dataContextValue.setRootState, 'error', t('SomethingWentWrong'), error.message)
            dataContextValue.setRootState((prev) => ({ ...prev, initialLoading: false }))
         }
      })()

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   return <DataContext.Provider value={dataContextValue}>{children}</DataContext.Provider>
}
